/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Divider, Fade, Stack, Typography } from '@mui/material';

import { BasicRoutes } from '../../constants/basicRoutes';
import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { authorizeUserByToken } from '../../store/auth/actions';

export const SsoCallbackPage = memo(() => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const [mutation, { isIdle, isLoading, isError, error }] = useMutation(AuthenticationApiService.loginOidcCallback, {
    onSuccess: async token => {
      dispatch(authorizeUserByToken(token.data.payload.accessToken, new Date(token.data.payload.expires)));
    },
    onError: error => {
      const message = (error as any)?.response?.data?.message;
      const errorKey =
        (typeof message === 'string'
          ? {
              oidc_organization_id_cookie_missing: 'cookie_missing',
              oidc_token_fetch_failed: 'token_fetch_failed',
              oidc_group_claim_validation_error: 'group_claim',
              oidc_email_collision: 'email_collision',
            }[message]
          : undefined) ?? 'uncaught';

      setErrorKey(errorKey);
    },
  });

  useEffect(() => {
    void mutation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack direction="column" height="100vh" alignItems="center" justifyContent="center" textAlign="center">
      {isIdle || isLoading ? (
        <Box>
          <Typography variant="body1">{t(`login:page.sso.callback.loading`)}</Typography>
        </Box>
      ) : null}

      {isError ? (
        <Box>
          <Divider sx={{ my: 2 }} flexItem orientation="horizontal" variant="middle">
            <Typography variant="body1">{t('login:page.sso.callback.failed')}</Typography>
          </Divider>
          <Typography variant="body1">{t('login:page.sso.callback.failed_detailed')}</Typography>

          <Box my={2}>
            <Typography>{t(`login:page.sso.callback.errors.${errorKey}.error`)}</Typography>
            <Typography>{t(`login:page.sso.callback.errors.${errorKey}.hint`)}</Typography>
          </Box>

          <Typography
            variant="subtitle1"
            color="tertiary"
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowError(v => !v)}
            data-cy="sso-callback-oidc-error-toggle"
          >
            {showError ? t(`login:page.sso.callback.hide_error`) : t(`login:page.sso.callback.show_error`)}
          </Typography>

          {showError ? (
            <Fade in={showError}>
              <Typography fontFamily="monospace" whiteSpace="pre" textAlign="left">
                {JSON.stringify((error as any)?.response?.data, undefined, 2)}
              </Typography>
            </Fade>
          ) : null}
        </Box>
      ) : null}

      <Button
        sx={{ my: 2 }}
        variant="outlined"
        onClick={() => navigate(BasicRoutes.LOGIN)}
        startIcon={<ArrowBackIcon />}
      >
        {t(`login:page.sso.callback.return_to_login`)}
      </Button>
    </Stack>
  );
});
